import React, { memo } from 'react';

/**
 * 作品区标题栏（设计稿 183:57 / 183:179 顶部 32px 行）
 * - 「全部」高亮 chip 52x32 bg #2D2D2D radius 8
 * - 「电商详情」80x32 / 「产品宣传KV」108x32 交错排列，未选中实色底 #121212 + stroke #363636 radius 8，chip 间距 4px
 * - 右侧搜索框 200x32（x=1200），实色底 #121212，placeholder 白 45% 12px
 * - 搜索图标规格差异：瀑布流 24x24 内缩 4px；视频区 12x12 内缩 10px
 * - chip 选中与搜索词由父级受控（状态提升到 WorksSection）：文档流哨兵与
 *   portal 吸顶条双实例渲染同一组件，共享状态保证两处交互完全同步
 */
const CHIP_ROW: { label: string; width: number }[] = [
  { label: '全部', width: 52 },
  { label: '电商详情', width: 80 },
  { label: '产品宣传KV', width: 108 },
  { label: '电商详情', width: 80 },
  { label: '产品宣传KV', width: 108 },
  { label: '电商详情', width: 80 },
  { label: '产品宣传KV', width: 108 },
  { label: '电商详情', width: 80 },
  { label: '产品宣传KV', width: 108 },
  { label: '电商详情', width: 80 },
];

const SEARCH_ICON_SPEC = {
  waterfall: { size: 24, inset: 4 },
  video: { size: 12, inset: 10 },
} as const;

export type WorksTitleBarVariant = keyof typeof SEARCH_ICON_SPEC;

interface WorksTitleBarProps {
  variant?: WorksTitleBarVariant;
  /** 选中 chip 下标（受控）：状态提升到 WorksSection，哨兵/吸顶条双实例共享 */
  activeChip: number;
  onChipChange: (index: number) => void;
  /** 搜索词（受控）：同上，双实例同步 */
  searchValue: string;
  onSearchChange: (value: string) => void;
}

const WorksTitleBarComponent: React.FC<WorksTitleBarProps> = ({
  variant = 'waterfall',
  activeChip,
  onChipChange,
  searchValue,
  onSearchChange,
}) => {
  const iconSpec = SEARCH_ICON_SPEC[variant];

  return (
    <div className="relative flex h-8 items-center">
      {/* 响应式：chip 组占满剩余宽度、窄屏横向滚动（隐藏滚动条），避免与右侧搜索框重叠 */}
      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {CHIP_ROW.map((chip, index) => {
          const active = index === activeChip;
          return (
            <button
              key={`${chip.label}-${index}`}
              type="button"
              onClick={() => onChipChange(index)}
              className="flex h-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-[14px] font-normal leading-[14px] text-white transition-colors"
              style={{
                width: chip.width,
                backgroundColor: active ? '#2D2D2D' : '#121212',
                border: active ? '1px solid transparent' : '1px solid #363636',
              }}
            >
              {chip.label}
            </button>
          );
        })}
      </div>

      {/* 搜索框：设计稿位于 x=1200，宽 200；flex 成员 shrink-0 始终贴右，窄屏不被压缩；实色底 #121212 遮住点阵 */}
      <div className="ml-4 h-8 w-[200px] shrink-0 rounded-lg border border-[#3D3D3D] bg-[#121212]">
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="请输入搜索内容"
          className="h-full w-full rounded-lg bg-transparent pl-3 pr-10 text-[12px] leading-[12px] text-white outline-none placeholder:text-white/45"
        />
        <img
          src="/icons/home/icon-search.svg"
          alt=""
          className="pointer-events-none absolute"
          style={{ width: iconSpec.size, height: iconSpec.size, right: iconSpec.inset, top: iconSpec.inset }}
        />
      </div>
    </div>
  );
};

export const WorksTitleBar = memo(WorksTitleBarComponent);
